import type { StatusBadgeTone } from '@/components/ui/status-badge';
import type { InventoryItemStatus } from './types';

/**
 * Canonical `StatusBadge` tone per inventory item status (LF-6150). One shared
 * map so every surface rendering an inventory status agrees — the surface picks
 * a semantic **tone** and lets `StatusBadge` resolve the colours (LF-6146)
 * instead of pasting classes. This is the role the former
 * `INVENTORY_STATUS_BADGE_COLORS` class map played (LF-5182); that map was
 * deleted in LF-6153 once its last consumer (`InventoryStatusBadge`) migrated.
 *
 * Tones follow the LF-6148 recipe's palette mapping — gray→neutral,
 * blue→info, green→success, purple→ai — so the migrated surfaces keep the
 * status distinctions LF-5182 established (notably `notified` vs `identified`,
 * which are different workflow stages and must stay visually distinct).
 */
export const INVENTORY_STATUS_BADGE_TONES: Record<
  InventoryItemStatus,
  StatusBadgeTone
> = {
  draft: 'neutral',
  notified: 'ai',
  identified: 'info',
  'not-held': 'neutral',
  collected: 'success',
  resolved: 'success',
  archived: 'neutral',
  confirmed: 'info',
  settled: 'success',
  'not-owed': 'neutral'
};

/**
 * Which inventory statuses render as a QUIET chip (VALE-1758).
 *
 * A quiet chip keeps its tone but carries it as a leading dot on a neutral
 * shell. It is for the status that is the EXPECTED state of most rows in a
 * list: an unadministered estate's liabilities are all unsettled, so colouring
 * every one of them turns colour into background texture and leaves nothing to
 * mark the row that actually needs attention. Eleven consecutive tinted
 * `Unsettled` chips was the reported case.
 *
 * **The rule is WHO THE ROW IS WAITING ON**, and it is shared with
 * `STATUS_TAG_QUIET` in `balance-sheet/current-view/status-tag-chip.tsx` — the
 * two maps cover different status vocabularies on different surfaces, so they
 * cannot be one map, but they must not encode two different rules.
 *
 * A row waiting on SOMEONE ELSE goes quiet: the solicitor cannot act on it, so
 * marking it spends attention on a row that has nowhere to go. A row waiting on
 * US stays loud, and so does every terminal success — a `collected` or
 * `settled` row is what the solicitor is scanning a wall of outstanding ones
 * for, and any caution/critical state is by definition the exception.
 *
 * The constraint that produced this rule, rather than the more obvious
 * "quiet everything in flight": **a variant whose whole purpose is to create an
 * exception cannot be applied to every member of a list.** Quieting the entire
 * in-flight cluster was tried, rendered, and read as deleting the column's
 * signal rather than focusing it.
 *
 * Enumerated over the full status union rather than listed, so a status added
 * later is a compile error here instead of an unreviewed default. Neutral
 * statuses are `false` because a neutral chip has no tone to demote.
 */
export const INVENTORY_STATUS_BADGE_QUIET: Record<
  InventoryItemStatus,
  boolean
> = {
  draft: false,
  // waiting on the institution
  notified: true,
  confirmed: true,
  // waiting on us to act
  identified: false,
  // terminal successes — what the reader is scanning for
  collected: false,
  resolved: false,
  settled: false,
  // neutral already
  'not-held': false,
  archived: false,
  'not-owed': false
};

/**
 * Which statuses a HANDOVER quiets, on top of {@link INVENTORY_STATUS_BADGE_QUIET}
 * (VALE-1854).
 *
 * **Same rule as its sibling, applied to a row whose answer has changed.** That
 * map asks *who is this row waiting on*; handing the work to someone outside the
 * firm changes the answer, because the firm is no longer the one being waited on.
 * A status that was loud only because WE had to act is, on a handed-over row,
 * pointing at work nobody here is going to do — the same thing the chase badges
 * VALE-1802 removed from Discovery were doing, one column across.
 *
 * **Terminal successes stay loud, and that is the half worth stating.** Quieting
 * everything on a handed-over row is the obvious move and it is wrong: a
 * `collected` or `settled` row is a RESULT sitting in the file, and passing it to
 * whoever is handling the matter is the useful thing left to do. That is the
 * chase-versus-read line `discovery/handling-visibility.ts` states for Discovery
 * rows, reaching the status column — Vale stops chasing, it does not stop knowing.
 *
 * Enumerated over the full union for the same reason its sibling is: a status
 * added later is a compile error here rather than an unreviewed default.
 */
export const INVENTORY_STATUS_QUIETED_BY_HANDOVER: Record<
  InventoryItemStatus,
  boolean
> = {
  // loud because WE had to act — handed over, nobody here is acting
  identified: true,
  // already quiet, or neutral with no tone to demote
  draft: false,
  notified: false,
  confirmed: false,
  // terminal successes — a result in the file, still worth reading
  collected: false,
  resolved: false,
  settled: false,
  'not-held': false,
  archived: false,
  'not-owed': false
};
